import React, { useEffect, useState } from 'react';
import { leaveEncashmentAPI } from '../../../services';
import { Button, Card, Select, Input, Badge, LoadingSpinner, ErrorMessage, SuccessMessage } from '../../UI';
import LedgerPreview from './encashment/LedgerPreview';
import { Save } from 'lucide-react';

const LEAVE_TYPES = [
  { value: 'earned', label: 'Earned Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'casual', label: 'Casual Leave' },
  { value: 'compensatory', label: 'Compensatory Off' },
];

const EXCESS_HANDLING_OPTIONS = [
  { value: 'auto_encash', label: 'Auto-encash the excess' },
  { value: 'lapse', label: 'Lapse the excess' },
  { value: 'block', label: 'Block further accrual' },
];

const emptyLeaveTypeCfg = () => ({
  eligible: false,
  minBalanceRetained: 0,
  carryForwardCap: 0,
  carryForwardExpiryMonths: 0,
  excessOverCapHandling: 'lapse',
});

const toDateInput = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

const EncashmentConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingSection, setSavingSection] = useState('');

  // Live preview inputs for the formula builder
  const [previewEmployeeId, setPreviewEmployeeId] = useState('');
  const [previewLeaveType, setPreviewLeaveType] = useState('earned');
  const [previewTrigger, setPreviewTrigger] = useState('annual');
  const [previewDays, setPreviewDays] = useState(1);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await leaveEncashmentAPI.getConfig();
      setConfig(res.data?.data || res.data);
    } catch (err) {
      setError(err.message || 'Failed to load encashment configuration');
    } finally {
      setLoading(false);
    }
  };

  const updateLeaveType = (leaveType, patch) => {
    setConfig((prev) => ({
      ...prev,
      leaveTypes: {
        ...prev.leaveTypes,
        [leaveType]: { ...(prev.leaveTypes[leaveType] || emptyLeaveTypeCfg()), ...patch },
      },
    }));
  };

  const withSaving = async (section, fn) => {
    setSavingSection(section);
    setError('');
    setSuccess('');
    try {
      const res = await fn();
      setConfig(res.data?.data || res.data);
      setSuccess('Saved successfully');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSavingSection('');
    }
  };

  const saveLeaveTypes = () => withSaving('leaveTypes', () => leaveEncashmentAPI.updateLeaveTypesConfig({
    leaveTypes: Object.fromEntries(LEAVE_TYPES.map(({ value }) => [
      value,
      {
        eligible: config.leaveTypes[value]?.eligible || false,
        minBalanceRetained: Number(config.leaveTypes[value]?.minBalanceRetained || 0),
      },
    ])),
  }));

  const saveCarryForward = () => withSaving('carryForward', () => leaveEncashmentAPI.updateCarryForwardConfig({
    leaveTypes: Object.fromEntries(LEAVE_TYPES.map(({ value }) => [
      value,
      {
        carryForwardCap: Number(config.leaveTypes[value]?.carryForwardCap || 0),
        carryForwardExpiryMonths: Number(config.leaveTypes[value]?.carryForwardExpiryMonths || 0),
        excessOverCapHandling: config.leaveTypes[value]?.excessOverCapHandling || 'lapse',
      },
    ])),
  }));

  const saveFormula = () => withSaving('formula', () => leaveEncashmentAPI.updateFormulaConfig({
    formulaMode: config.formulaMode,
    sharedFormula: config.sharedFormula,
    formulasByTrigger: config.formulasByTrigger,
  }));

  const saveTriggers = () => withSaving('triggers', () => leaveEncashmentAPI.updateTriggersConfig({
    triggers: config.triggers,
  }));

  const saveApproval = () => withSaving('approval', () => leaveEncashmentAPI.updateApprovalConfig(config.approval));

  const updateFormula = (trigger, patch) => {
    setConfig((prev) => {
      if (prev.formulaMode === 'shared') {
        return { ...prev, sharedFormula: { ...prev.sharedFormula, ...patch } };
      }
      return {
        ...prev,
        formulasByTrigger: {
          ...prev.formulasByTrigger,
          [trigger]: { ...prev.formulasByTrigger[trigger], ...patch },
        },
      };
    });
  };

  const updateFormulaComponent = (trigger, component, checked) => {
    const target = config.formulaMode === 'shared' ? config.sharedFormula : config.formulasByTrigger[trigger];
    updateFormula(trigger, { components: { ...target.components, [component]: checked } });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  }

  if (!config) {
    return <ErrorMessage message={error || 'Failed to load configuration'} />;
  }

  const activeFormula = config.formulaMode === 'shared' ? config.sharedFormula : config.formulasByTrigger[previewTrigger];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Leave Encashment Configuration</h1>
        <p className="text-secondary-600">Configure eligible leave types, the encashment formula, carry-forward rules, trigger windows and the approval workflow</p>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}
      {success && <SuccessMessage message={success} onDismiss={() => setSuccess('')} />}

      {/* Eligible Leave Types */}
      <Card title="Eligible Leave Types">
        <div className="space-y-3">
          {LEAVE_TYPES.map(({ value, label }) => {
            const cfg = config.leaveTypes[value] || emptyLeaveTypeCfg();
            return (
              <div key={value} className="flex flex-wrap items-center gap-4 p-3 border border-secondary-200 rounded-lg">
                <label className="flex items-center gap-2 min-w-[180px]">
                  <input
                    type="checkbox"
                    checked={!!cfg.eligible}
                    onChange={(e) => updateLeaveType(value, { eligible: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-medium text-secondary-900">{label}</span>
                </label>
                <Input
                  label="Min Balance to Retain (days)"
                  type="number"
                  min="0"
                  step="0.5"
                  value={cfg.minBalanceRetained}
                  onChange={(e) => updateLeaveType(value, { minBalanceRetained: e.target.value })}
                  disabled={!cfg.eligible}
                  containerClassName="w-56"
                />
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveLeaveTypes} loading={savingSection === 'leaveTypes'}>
            <Save className="w-4 h-4 mr-2" /> Save Leave Types
          </Button>
        </div>
      </Card>

      {/* Formula Builder */}
      <Card title="Encashment Formula">
        <p className="text-sm text-secondary-600 mb-3">
          Amount = (Selected Salary Components ÷ Divisor) × Days Encashed
        </p>
        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={config.formulaMode === 'shared'}
              onChange={() => setConfig((p) => ({ ...p, formulaMode: 'shared' }))}
            />
            One formula for all triggers
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={config.formulaMode === 'per_trigger'}
              onChange={() => setConfig((p) => ({ ...p, formulaMode: 'per_trigger' }))}
            />
            Distinct formula per trigger
          </label>
        </div>

        {config.formulaMode === 'per_trigger' && (
          <Select
            label="Editing formula for trigger"
            value={previewTrigger}
            onChange={(e) => setPreviewTrigger(e.target.value)}
            containerClassName="w-56 mb-4"
          >
            <option value="annual">Annual Window</option>
            <option value="resignation">Resignation / F&F</option>
            <option value="retirement">Retirement</option>
          </Select>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm font-medium text-secondary-700 mb-2">Salary Components</p>
            <div className="flex flex-wrap gap-4 mb-4">
              {['basic', 'da', 'hra', 'special'].map((c) => (
                <label key={c} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!activeFormula?.components?.[c]}
                    onChange={(e) => updateFormulaComponent(previewTrigger, c, e.target.checked)}
                  />
                  <span className="capitalize">{c === 'da' ? 'DA' : c === 'hra' ? 'HRA' : c}</span>
                </label>
              ))}
            </div>
            <Input
              label="Divisor"
              type="number"
              min="0.01"
              step="0.01"
              value={activeFormula?.divisor ?? ''}
              onChange={(e) => updateFormula(previewTrigger, { divisor: e.target.value })}
              containerClassName="w-40"
            />
            {Number(activeFormula?.divisor) <= 0 && (
              <p className="text-sm text-danger-600 mt-1">Divisor must be greater than 0</p>
            )}
            <div className="mt-4">
              <Button onClick={saveFormula} loading={savingSection === 'formula'}>
                <Save className="w-4 h-4 mr-2" /> Save Formula
              </Button>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-secondary-700 mb-2">Live Preview</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Input
                label="Sample Employee ID"
                value={previewEmployeeId}
                onChange={(e) => setPreviewEmployeeId(e.target.value)}
                placeholder="Paste an employee _id"
              />
              <Select label="Leave Type" value={previewLeaveType} onChange={(e) => setPreviewLeaveType(e.target.value)}>
                {LEAVE_TYPES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </Select>
              <Input
                label="Days"
                type="number"
                min="0.5"
                step="0.5"
                value={previewDays}
                onChange={(e) => setPreviewDays(e.target.value)}
              />
            </div>
            <LedgerPreview
              employeeId={previewEmployeeId}
              leaveType={previewLeaveType}
              triggerType={config.formulaMode === 'shared' ? 'annual' : previewTrigger}
              daysRequested={previewDays}
            />
          </div>
        </div>
      </Card>

      {/* Carry-Forward Rules */}
      <Card title="Carry-Forward Rules">
        <div className="space-y-3">
          {LEAVE_TYPES.filter(({ value }) => config.leaveTypes[value]?.eligible).map(({ value, label }) => {
            const cfg = config.leaveTypes[value] || emptyLeaveTypeCfg();
            return (
              <div key={value} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-3 border border-secondary-200 rounded-lg">
                <span className="font-medium text-secondary-900">{label}</span>
                <Input
                  label="Max Carry-Forward (days)"
                  type="number"
                  min="0"
                  value={cfg.carryForwardCap}
                  onChange={(e) => updateLeaveType(value, { carryForwardCap: e.target.value })}
                />
                <Input
                  label="Validity (months, 0 = never expires)"
                  type="number"
                  min="0"
                  value={cfg.carryForwardExpiryMonths}
                  onChange={(e) => updateLeaveType(value, { carryForwardExpiryMonths: e.target.value })}
                />
                <Select
                  label="Excess-Over-Cap Handling"
                  value={cfg.excessOverCapHandling}
                  onChange={(e) => updateLeaveType(value, { excessOverCapHandling: e.target.value })}
                >
                  {EXCESS_HANDLING_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </div>
            );
          })}
          {LEAVE_TYPES.every(({ value }) => !config.leaveTypes[value]?.eligible) && (
            <p className="text-sm text-secondary-500">Mark at least one leave type as eligible above to configure carry-forward rules.</p>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveCarryForward} loading={savingSection === 'carryForward'}>
            <Save className="w-4 h-4 mr-2" /> Save Carry-Forward Rules
          </Button>
        </div>
      </Card>

      {/* Trigger Windows */}
      <Card title="Trigger Windows">
        <div className="space-y-4">
          <div className="p-3 border border-secondary-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="font-medium text-secondary-900">Annual Encashment Window</span>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!config.triggers.annual.enabled}
                  onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, annual: { ...p.triggers.annual, enabled: e.target.checked } } }))}
                />
                Enabled
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                label="Window Opens"
                type="date"
                value={toDateInput(config.triggers.annual.windowOpenDate)}
                onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, annual: { ...p.triggers.annual, windowOpenDate: e.target.value } } }))}
              />
              <Input
                label="Window Closes"
                type="date"
                value={toDateInput(config.triggers.annual.windowCloseDate)}
                onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, annual: { ...p.triggers.annual, windowCloseDate: e.target.value } } }))}
              />
              <Input
                label="Max Days per Year"
                type="number"
                min="0"
                value={config.triggers.annual.maxDaysPerYear}
                onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, annual: { ...p.triggers.annual, maxDaysPerYear: e.target.value } } }))}
              />
            </div>
          </div>

          {['resignation', 'retirement'].map((trigger) => (
            <div key={trigger} className="p-3 border border-secondary-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-secondary-900 capitalize">
                  {trigger === 'resignation' ? 'Resignation / Full & Final' : 'Retirement'}
                </span>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!config.triggers[trigger].enabled}
                      onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, [trigger]: { ...p.triggers[trigger], enabled: e.target.checked } } }))}
                    />
                    Enabled
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!config.triggers[trigger].skipApproval}
                      onChange={(e) => setConfig((p) => ({ ...p, triggers: { ...p.triggers, [trigger]: { ...p.triggers[trigger], skipApproval: e.target.checked } } }))}
                    />
                    Skip manual approval
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveTriggers} loading={savingSection === 'triggers'}>
            <Save className="w-4 h-4 mr-2" /> Save Trigger Windows
          </Button>
        </div>
      </Card>

      {/* Approval Workflow */}
      <Card title="Approval Workflow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Auto-Approve Threshold (days)"
            type="number"
            min="0"
            value={config.approval.autoApproveUnderDays}
            onChange={(e) => setConfig((p) => ({ ...p, approval: { ...p.approval, autoApproveUnderDays: e.target.value } }))}
          />
          <Select
            label="Approval Chain"
            value={config.approval.chain}
            onChange={(e) => setConfig((p) => ({ ...p, approval: { ...p.approval, chain: e.target.value } }))}
          >
            <option value="hr_only">HR only</option>
            <option value="manager_hr">Manager → HR</option>
          </Select>
        </div>
        <p className="text-sm text-secondary-500 mt-2">
          Requests at or below the threshold move straight to <Badge variant="success">Approved</Badge>. Resignation/retirement-triggered requests may separately skip approval via their own toggle above.
        </p>
        <div className="mt-4 flex justify-end">
          <Button onClick={saveApproval} loading={savingSection === 'approval'}>
            <Save className="w-4 h-4 mr-2" /> Save Approval Workflow
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EncashmentConfigPage;
